'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, Package, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { handoverScanningQueries } from '../services/handover-scanning.queries';
import type { ScanningContainer } from '../types';
import { ContainerStatusBadge } from './container-status-badge';

interface ReceivingListPanelProps {
  selectedContainerIds: number[];
  onToggleContainer: (id: number) => void;
  /** Receives the ids of every currently-loaded container. */
  onSelectAll: (ids: number[]) => void;
  onClearSelection: () => void;
}

export function ReceivingListPanel({
  selectedContainerIds,
  onToggleContainer,
  onSelectAll,
  onClearSelection
}: ReceivingListPanelProps) {
  const t = useTranslations('handoverScanning');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const {
    items: containers,
    isLoading,
    isFetchingNextPage,
    loadMoreRef
  } = useInfiniteScroll<ScanningContainer>(
    handoverScanningQueries.receivingInfiniteList(
      debouncedSearch ? { search: debouncedSearch } : {}
    )
  );

  const allSelected =
    containers.length > 0 &&
    containers.every((c) => selectedContainerIds.includes(c.id));
  const someSelected =
    !allSelected && containers.some((c) => selectedContainerIds.includes(c.id));

  function handleSelectAllToggle() {
    if (allSelected) {
      onClearSelection();
    } else {
      onSelectAll(containers.map((c) => c.id));
    }
  }

  return (
    <Card className='flex h-full flex-col'>
      <CardHeader className='space-y-3 pb-3'>
        <div className='flex items-center gap-2'>
          <Package className='text-muted-foreground h-5 w-5' />
          <CardTitle className='text-base'>{t('panelReceivingList')}</CardTitle>
          <Badge variant='secondary' className='ms-auto'>
            {containers.length} {t('shipmentLabel')}
          </Badge>
        </div>

        {/* Select all toggle */}
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <Checkbox
              checked={allSelected || (someSelected ? 'indeterminate' : false)}
              onCheckedChange={handleSelectAllToggle}
              aria-label={t('selectAll')}
            />
            <span className='text-muted-foreground text-sm'>
              {t('selectAll')}
            </span>
          </div>
          {selectedContainerIds.length > 0 && (
            <Button
              variant='ghost'
              size='sm'
              onClick={onClearSelection}
              className='ms-auto h-auto py-0 text-xs'
            >
              {t('clearSelection')}
            </Button>
          )}
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
          <Input
            placeholder={t('searchContainers')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='ps-9'
          />
        </div>
      </CardHeader>

      <CardContent
        className='flex-1 overflow-y-auto py-4'
        style={{ maxHeight: 'calc(100vh - 350px)' }}
      >
        {isLoading ? (
          <div className='space-y-3 pt-3'>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className='h-28 w-full rounded-lg' />
            ))}
          </div>
        ) : containers.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <Package className='text-muted-foreground mb-3 h-10 w-10' />
            <p className='text-muted-foreground text-sm'>
              {t('noContainersFound')}
            </p>
          </div>
        ) : (
          <div className='space-y-3 pt-3'>
            {containers.map((container) => {
              const isSelected = selectedContainerIds.includes(container.id);
              return (
                <div
                  key={container.id}
                  role='button'
                  tabIndex={0}
                  onClick={() => onToggleContainer(container.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleContainer(container.id);
                    }
                  }}
                  className={cn(
                    'w-full cursor-pointer rounded-lg border p-3 text-start transition-all',
                    'hover:border-primary/50 hover:bg-primary/5',
                    isSelected && 'ring-primary bg-primary/5 ring-2'
                  )}
                >
                  <div className='flex items-start gap-3'>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleContainer(container.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={container.container_number ?? undefined}
                      className='mt-0.5'
                    />
                    <div className='min-w-0 flex-1 space-y-1.5'>
                      {/* Top row: number + status */}
                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <span className='text-sm font-semibold'>
                          {container.container_number}
                        </span>
                        <ContainerStatusBadge status={container.status} />
                      </div>

                      {/* Entity name */}
                      {container.entity_name && (
                        <p className='text-muted-foreground truncate text-xs'>
                          {container.entity_name}
                        </p>
                      )}

                      {/* Box info */}
                      {container.box && (
                        <p className='text-xs font-medium'>
                          {container.box.box_number}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div ref={loadMoreRef} className='h-px' />
            {isFetchingNextPage && (
              <div className='flex items-center justify-center py-2'>
                <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
